import { axios } from '@/api/axios.ts';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import type {
  TConditionalQuestionResponse,
  TCreateConditionalQuestion,
  TCreateSurveyQuestion,
  TCreateSurveySection,
  TCreateSurveySectionResponse,
  TCreateSurveyVersion,
  TDeleteSurvey,
  TQuestion,
  TSubmitMultipleSurveyResponse,
  TSurvey,
  TSurveyVersion,
} from './survey.types.ts';

export const surveyApi = {
  //Survey Management
  getCurrentSurvey: async () => {
    const response = await axios.get<TSurvey>(`survey`);

    return response.data;
  },
  getMyResponses: async () => {
    const response = await axios.get<TSurvey | null>(`survey/responses/me`);

    return response.data;
  },
  getAllSurveyVersions: async () => {
    const response = await axios.get<TSurveyVersion[]>(`survey/versions`);

    return response.data;
  },
  getSurveyVersion: async (version: number) => {
    const response = await axios.get<TSurvey>(`survey/versions/${version}`);

    return response.data;
  },
  createSurveyVersion: async () => {
    const response = await axios.post<TCreateSurveyVersion>(`survey`);

    return response.data;
  },
  saveStatusSurvey: async (payload: { status: string }) => {
    const { status } = payload;
    const response = await axios.patch<TSurvey>(`survey/${status}`);

    return response.data;
  },
  //Survey Sections
  createSurveySection: async (payload: { section: TCreateSurveySection }) => {
    const { section } = payload;
    const response = await axios.post<TCreateSurveySectionResponse>(`survey/sections`, section);

    return response.data;
  },
  updateSurveySection: async (payload: { id: number; section: Partial<TCreateSurveySection> }) => {
    const { id, section } = payload;
    const response = await axios.patch<TCreateSurveySectionResponse>(
      `survey/sections/${id}`,
      section,
    );

    return response.data;
  },
  deleteSurveySection: async (payload: { id: number }) => {
    const { id } = payload;
    const response = await axios.delete<TDeleteSurvey>(`survey/sections/${id}`);

    return response.data;
  },
  reorderSurveySections: async (payload: { items: number[] }) => {
    const { items } = payload;
    const response = await axios.patch<{ message: string }>(`survey/sections/reorder`, { items });

    return response.data;
  },
  //Survey Questions
  createSurveyQuestion: async (payload: { sectionId: number; question: TCreateSurveyQuestion }) => {
    const { sectionId, question } = payload;

    const response = await axios.post<TQuestion>(
      `survey/sections/${sectionId}/questions`,
      question,
    );

    return response.data;
  },
  updateSurveyQuestion: async (payload: {
    sectionId: number;
    questionId: number;
    question: Partial<TQuestion>;
  }) => {
    const { sectionId, questionId, question } = payload;
    const response = await axios.patch<TQuestion>(
      `survey/sections/${sectionId}/questions/${questionId}`,
      question,
    );

    return response.data;
  },
  deleteSurveyQuestion: async (payload: { sectionId: number; questionId: number }) => {
    const { sectionId, questionId } = payload;
    const response = await axios.delete<TDeleteSurvey>(
      `survey/sections/${sectionId}/questions/${questionId}`,
    );

    return response.data;
  },
  reorderSurveyQuestions: async (payload: { sectionId: number; items: number[] }) => {
    const { sectionId, items } = payload;
    const response = await axios.patch<{ message: string }>(
      `survey/sections/${sectionId}/questions/reorder`,
      { items },
    );

    return response.data;
  },
  //Survey Question Conditional
  createConditionalQuestion: async (payload: {
    sectionId: number;
    questionId: number;
    condition: TCreateConditionalQuestion;
  }) => {
    const { sectionId, questionId, condition } = payload;
    const response = await axios.post<TConditionalQuestionResponse>(
      `survey/sections/${sectionId}/questions/${questionId}/conditionals`,
      condition,
    );

    return response.data;
  },
  updateConditionalQuestion: async (payload: {
    sectionId: number;
    questionId: number;
    id: number;
    condition: Partial<TCreateConditionalQuestion>;
  }) => {
    const { sectionId, questionId, id, condition } = payload;
    const response = await axios.patch<TConditionalQuestionResponse>(
      `survey/sections/${sectionId}/questions/${questionId}/conditionals/${id}`,
      condition,
    );

    return response.data;
  },
  deleteConditionalQuestion: async (payload: {
    sectionId: number;
    questionId: number;
    id: number;
  }) => {
    const { sectionId, questionId, id } = payload;
    const response = await axios.delete<TDeleteSurvey>(
      `survey/sections/${sectionId}/questions/${questionId}/conditionals/${id}`,
    );

    return response.data;
  },
  reorderConditionalQuestions: async (payload: {
    sectionId: number;
    questionId: number;
    items: number[];
  }) => {
    const { sectionId, questionId, items } = payload;
    const response = await axios.patch<{ message: string }>(
      `survey/sections/${sectionId}/questions/${questionId}/conditionals/reorder`,
      { items },
    );

    return response.data;
  },
  //Survey Export
  exportSurveyPdf: async (clientId: number) => {
    const response = await axios.get<Blob>(`survey/responses/export/pdf`, {
      params: { client_id: clientId },
      responseType: 'blob',
    });

    return response;
  },
  //Survey Response
  submitMultipleSurvey: async ({
    payload,
    section_id,
  }: {
    payload: { question_id: number; response: string | string[] }[];
    section_id: number;
  }) => {
    const url = payload?.length
      ? 'survey/responses/multiple'
      : `survey/responses/multiple?null_section=${section_id}`;
    const response = await axios.post<TSubmitMultipleSurveyResponse[]>(url, payload);

    return response.data;
  },
};

export const surveyQueries = createQueryKeys('survey', {
  getCurrentSurvey: () => ({
    queryKey: ['current'],
    queryFn: () => surveyApi.getCurrentSurvey(),
  }),
  getMyResponses: () => ({
    queryKey: ['my-responses'],
    queryFn: () => surveyApi.getMyResponses(),
  }),
  getAllSurveyVersions: () => ({
    queryKey: ['versions'],
    queryFn: () => surveyApi.getAllSurveyVersions(),
  }),
  getSurveyVersion: (version: number) => ({
    queryKey: ['version', version],
    queryFn: () => surveyApi.getSurveyVersion(version),
  }),
});
